import { useCallback, useEffect, useMemo, useState } from "react";
import { Input } from "./ui/input";
import { Button } from "./ui/button";
import {
  deploymentAvailaility,
  getPackages,
  getVersions,
  postChangepermissions,
  postCleanup,
  postCreatedatabase,
  postCreatedomain,
  postDeployment,
} from "@/services/deployment.service";
import Dropdown, { dropdownCss, DropdownInterface } from "./ui/dropdown";
import { DeploymentFormSchema } from "@/schemas/deployment-form.schema";
import { useForm } from "react-hook-form";
import { z } from "zod";
import { zodResolver } from "@hookform/resolvers/zod";
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "./ui/form";
import { Progress } from "./ui/progress";
import { getCompanies } from "@/services/companies.service";
import { getPaymentPackages } from "@/services/paymentPackages.service";
import { getNormalDomains } from "@/services/domains.service";
import { DomainSchema } from "@/schemas/domain.schema";
import { ToggleInput, ToggleLabel, ToggleSpan } from "./ui/toggle";
import { getApprovedConnections } from "@/services/ftpConnections.service";
import { DeploymentVersionSchema } from "@/schemas/versions.schema";

enum IsDeploying {
  notDeploying = 0,
  deploying = 1,
  deploymentDone = 2,
  authFailed = 3,
  deploymentFailed = 4,
}

/**
 * TODO: Temporarily setting props to optional
 * to prevent errors when calling this component
 */
interface Props {
  onSubmit?: (data: z.infer<typeof DeploymentFormSchema>) => void;
  isLoading?: boolean;
}

export default function DeploymentForm(props: Props) {
  const [progress, setProgress] = useState(0);
  const [currentStepIndex, setCurrentStepIndex] = useState(-1);
  const [logs, setLogs] = useState<string[]>([]);

  const [versions, setVersions] = useState<DropdownInterface[]>([]);
  const [packages, setPackages] = useState<DropdownInterface[]>([]);
  const [isDeploying, setIsDeploying] = useState(IsDeploying.notDeploying);
  const [globalFormData, setGlobalFormData] =
    useState<z.infer<typeof DeploymentFormSchema>>();
  const [clearbookDetails, setClearbookDetails] = useState<Map<string, string>>();
  const [companies, setCompanies] = useState<DropdownInterface[]>([]);
  const [paymentPackages, setPaymentPackages] = useState<DropdownInterface[]>([]);
  const [domainUrl, setDomainUrl] = useState("");
  const [currentDomains, setCurrentDomains] = useState<z.infer<typeof DomainSchema>[]>([]);
  const [domainAlreadyExists, setDomainAlreadyExists] = useState(false);
  const [isTestDomain, setIsTestDomain] = useState(false);
  const [approvedConnections, setApprovedConnections] = useState<DropdownInterface[]>([]);
  const [clearbookVersionDetailsArray, setClearbookVersionDetailsArray] = useState<z.infer<typeof DeploymentVersionSchema>[]>([]);

  const steps = useMemo(() => [
    {
      title: "Create domain",
      expectedReturn: "Domain created",
      functionToRun: postCreatedomain,
    },
    {
      title: "Create database",
      expectedReturn: "Database created",
      functionToRun: postCreatedatabase,
    },
    {
      title: "Initiate deployment",
      expectedReturn: "Successfully Deployed",
      functionToRun: postDeployment,
    },
    {
      title: "Clean up",
      expectedReturn: "Cleanup Done",
      functionToRun: postCleanup,
    },
    {
      title: "Updating Permissions",
      expectedReturn: "Permissions Updated",
      functionToRun: postChangepermissions,
    },
    { title: "Done" },
  ], []);

  const form = useForm<z.infer<typeof DeploymentFormSchema>>({
    resolver: zodResolver(DeploymentFormSchema),
    defaultValues: {
      name: "",
      website: "clearbook.dk",
      bookingFolder: "booking/",
      adminFolder: "admin",
      email: "",
      databaseServer: "localhost",
      databaseName: "",
      databaseUser: "",
      databasePassword: "",
    },
  });

  const loadCurrentDomains = useCallback(async () => {
    const domains = await getNormalDomains();
    setCurrentDomains(domains);
  }, []);

  const loadAllVersions = useCallback(async () => {
    const fetchedVersions = await getVersions();
    const resultArray = Object.keys(fetchedVersions).map(function (versionIndex) {
      const version = fetchedVersions[Number(versionIndex)];
      return {id: Number(version['version']), value: version['version'], text: version['version']};
    });
    setClearbookVersionDetailsArray(fetchedVersions);
    setVersions(resultArray); // TODO: Fix type or use zod schema
  }, []);

  const loadAllPackages = useCallback(async () => {
    const fetchedPackage = await getPackages();
    const resultArray: DropdownInterface[] = fetchedPackage.map(clearBookPackage => {
      return {id: Number(clearBookPackage.ID), value: clearBookPackage.Type, text: clearBookPackage.Type}
    })
    setPackages(resultArray);
  }, []);

  const loadAllCompanies = useCallback(async () => {
    const companiesResult: DropdownInterface[] = (await getCompanies()).map((company) => {
      return {id: Number(company.ID), value: company.ID, text: company.Name};
    });
    setCompanies(companiesResult);
  }, []);

  const loadAllPaymentPackages = useCallback(async () => {
    const paymentPackagesResult: DropdownInterface[] = (await getPaymentPackages()).map((paymentPackage) => {
      return {id: Number(paymentPackage.ID), value: paymentPackage.ID, text: paymentPackage.Name};
    });
    setPaymentPackages(paymentPackagesResult);
  }, []);

  const loadAllApprovedConnections = useCallback(async () => {
    const approvedConnectionsResult: DropdownInterface[] = (await getApprovedConnections()).map((connection) => {
      return {id: Number(connection.ID), value: connection.ID, text: connection.Name};
    });
    setApprovedConnections(approvedConnectionsResult);
  }, []);

  function checkIfDomainAlreadyExists(bookingFolder:string){
    const result = currentDomains.filter(domain => {
      return (domain.Folder == bookingFolder);
    });

    return result.length > 0;
  }

  async function checkAvailability(data: z.infer<typeof DeploymentFormSchema>) {
    const result = await deploymentAvailaility();
    if (result === false) {
      setIsDeploying(IsDeploying.authFailed);
    } else {
      onSubmit(data);
    }
  }

  async function onSubmit(data: z.infer<typeof DeploymentFormSchema>) {
    if(checkIfDomainAlreadyExists(data.bookingFolder)){
      setDomainAlreadyExists(true);
      return;
    }

    const clearBookVersion = (
      document.getElementById("clearbookVersion") as HTMLInputElement
    ).value;
    const clearbookPackage = (
      document.getElementById("clearbookPackage") as HTMLInputElement
    ).value;
    const companyName = (
      document.getElementById("company") as HTMLInputElement
    ).value;
    const clearbookPaymentPackage = (
      document.getElementById("clearbookPaymentPackage") as HTMLInputElement
    ).value;
    const ftpConnection = (
      document.getElementById("clearbookFTPConnection") as HTMLInputElement
    ).value;

    const clearbookDetails = new Map<string, string>();
    clearbookDetails.set('companyID', companyName);
    clearbookDetails.set('clearbookVersion', clearBookVersion);
    clearbookDetails.set('package', clearbookPackage);
    clearbookDetails.set('paymentPackage', clearbookPaymentPackage);
    clearbookDetails.set('clearbookFTPConnection', ftpConnection);
    setClearbookDetails(clearbookDetails);
    
    const result = await deploymentAvailaility();

    if (result === false) {
      setIsDeploying(IsDeploying.authFailed);
    } else {
      setGlobalFormData(data);
      setIsDeploying(IsDeploying.deploying);
      startDeployment();
    }
  }

  const prependLog = useCallback((log: string) => {
    setLogs((logs) => [log, ...logs]);
  }, []);

  const nextStep = useCallback(() => {
    setCurrentStepIndex((index) => index + 1);
  }, []);

  const formatFormData = useCallback(
    (data: z.infer<typeof DeploymentFormSchema> | undefined) => {
      const formData = new FormData();
      formData.append("domainName", data!.name);
      formData.append("domainWebsite", data!.website);
      formData.append("bookingFolder", data!.bookingFolder);
      formData.append("adminFolder", data!.adminFolder);
      formData.append("ownerEmail", data!.email);
      formData.append("databaseServer", data!.databaseServer);
      formData.append("databaseName", data!.databaseName);
      formData.append("databaseUser", data!.databaseUser);
      formData.append("databasePassword", data!.databasePassword);
      formData.append("isTestDomain", isTestDomain.toString());

      clearbookDetails?.forEach((value, key) => {
          formData.append(key, value);
      });
      return formData;
    },
    [clearbookDetails, isTestDomain]
  );

  function startDeployment() {
    if (currentStepIndex < 0) {
      setCurrentStepIndex((index) => index + 1);
    }
  }

  const startStep = useCallback(async () => {
    setProgress((currentStepIndex / steps.length) * 100);
    if (currentStepIndex < steps.length - 1) {
      prependLog(steps[currentStepIndex].title);
      if (steps[currentStepIndex].functionToRun != undefined) {
        let result = null;
        try {
          result = await steps[currentStepIndex].functionToRun(
            formatFormData(globalFormData)
          );
        } catch (error) {
          console.log("Error Occured: ", error);
        }
        if (result == steps[currentStepIndex].expectedReturn) {
          nextStep();
        }
      } else {
        nextStep();
      }
    } else {
      setDomainUrl("https://clearbook.dk/".concat(globalFormData!.bookingFolder));
      prependLog(steps[currentStepIndex].title);
      setProgress(100);
      setIsDeploying(IsDeploying.deploymentDone);
    }
  }, [
    currentStepIndex,
    steps,
    prependLog,
    formatFormData,
    globalFormData,
    nextStep,
    setDomainUrl,
    setProgress,
    setIsDeploying,
  ]);

  useEffect(() => {
    if (currentStepIndex > -1) {
      startStep();
    }
    // Haven't put the startStep on purpose because it the change of scope for the function
   }, [currentStepIndex, startStep]);

  // Load initial data only once
  useEffect(() => {
    if (versions.length === 0) {
      loadAllVersions();
    }
  }, [versions.length, loadAllVersions]);

  useEffect(() => {
    if (packages.length === 0) {
      loadAllPackages();
    }
  }, [packages.length, loadAllPackages]);

  useEffect(() => {
    if (companies.length === 0) {
      loadAllCompanies();
    }
  }, [companies.length, loadAllCompanies]);

  useEffect(() => {
    if (paymentPackages.length === 0) {
      loadAllPaymentPackages();
    }
  }, [paymentPackages.length, loadAllPaymentPackages]);

  useEffect(() => {
    if (currentDomains.length === 0) {
      loadCurrentDomains();
    }
  }, [currentDomains.length, loadCurrentDomains]);

  useEffect(() => {
    if (approvedConnections.length === 0) {
      loadAllApprovedConnections();
    }
  }, [approvedConnections.length, loadAllApprovedConnections]);

  function getClearbookVersionDetails(version: string){
    var details = "";
    Object.keys(clearbookVersionDetailsArray).map(function (versionIndex) {
      if(clearbookVersionDetailsArray[Number(versionIndex)].version == version){
        details = clearbookVersionDetailsArray[Number(versionIndex)].details;
      }
    });
    document.getElementById("clearbookVersionDetailContainer")!.innerHTML = details;
  }

  return (
    <div>
      {(isDeploying === IsDeploying.notDeploying ||
        isDeploying === IsDeploying.authFailed) && (
        <Form {...form}>
          <form
            className="grid gap-y-4"
            onSubmit={form.handleSubmit(checkAvailability)}
          >
            <div className="grid grid-cols-6 gap-4">
              <div className="col-start-1 col-end-4 rounded-md border-black">
                <h1 className="text-xl font-bold text-black dark:text-white mb-2">
                  Domain
                </h1>
                <FormField
                  control={form.control}
                  name="name"
                  render={({ field }) => (
                    <FormItem className="mb-2">
                      <FormLabel>Name</FormLabel>
                      <FormControl>
                        <Input
                          disabled={props.isLoading}
                          placeholder="Name"
                          {...field}
                        />
                      </FormControl>

                      <FormMessage />
                    </FormItem>
                  )}
                />
                <FormField
                  control={form.control}
                  name="website"
                  render={({ field }) => (
                    <FormItem className="mb-2">
                      <FormLabel>Website</FormLabel>
                      <FormControl>
                        <Input
                          disabled={props.isLoading}
                          readOnly
                          placeholder="Website"
                          {...field}
                        />
                      </FormControl>

                      <FormMessage />
                    </FormItem>
                  )}
                />
                <FormField
                  control={form.control}
                  name="bookingFolder"
                  render={({ field }) => (
                    <FormItem className="mb-2">
                      <FormLabel>Booking Folder</FormLabel>
                      <FormLabel className="italic font-normal text-black/50">
                        https://clearbook.dk/booking/(Booking Folder)
                      </FormLabel>
                      <FormControl>
                        <Input
                          disabled={props.isLoading}
                          placeholder="Booking Folder"
                          {...field}
                        />
                      </FormControl>

                      <FormMessage />
                      {domainAlreadyExists && (
                        <FormMessage>
                          Domain Already Exists
                        </FormMessage>
                      )}
                    </FormItem>
                  )}
                />
                <FormField
                  control={form.control}
                  name="adminFolder"
                  render={({ field }) => (
                    <FormItem className="mb-2">
                      <FormLabel>Admin Folder</FormLabel>
                      <FormLabel className="italic font-normal text-black/50">
                        https://clearbook.dk/booking/(Booking Folder)/admin
                      </FormLabel>
                      <FormControl>
                        <Input
                          disabled={props.isLoading}
                          readOnly
                          placeholder="Admin Folder"
                          {...field}
                        />
                      </FormControl>

                      <FormMessage />
                    </FormItem>
                  )}
                />
                <FormField
                  control={form.control}
                  name="email"
                  render={({ field }) => (
                    <FormItem className="mb-2">
                      <FormLabel>Email</FormLabel>
                      <FormControl>
                        <Input
                          disabled={props.isLoading}
                          placeholder="Email"
                          {...field}
                        />
                      </FormControl>

                      <FormMessage />
                    </FormItem>
                  )}
                />
                <FormItem className="mb-2">
                  <FormLabel>Company</FormLabel>
                  <FormControl>
                    <Dropdown
                      id="company"
                      title="Company"
                      className={dropdownCss}
                    >
                      {companies}
                    </Dropdown>
                  </FormControl>
                  <FormMessage />
                </FormItem>
                <ToggleLabel className="ps-0">
                  <span className="p-1">
                    Is test domain?
                  </span>
                  <ToggleInput checked={isTestDomain} onChange={() => {
                      setIsTestDomain(!isTestDomain);
                    }}/>
                  <ToggleSpan/>
                </ToggleLabel>
                <h1 className="text-xl font-bold text-black dark:text-white mb-2">
                  Version
                </h1>
                <FormItem className="mb-2">
                  <FormLabel>ClearBook Version</FormLabel>
                  <FormControl>
                    <Dropdown
                      id="clearbookVersion"
                      title="Versions"
                      className={dropdownCss}
                      onChange={(e) => {
                        getClearbookVersionDetails(e.currentTarget.value);
                      }}
                    >
                      {versions}
                    </Dropdown>
                  </FormControl>

                  <FormMessage />
                </FormItem>
                <h2>Details:</h2>
                <div id="clearbookVersionDetailContainer">

                </div>
              </div>
              <div className="col-start-4 col-end-7 rounded-md border-black">
                <h1 className="text-xl font-bold text-black dark:text-white mb-2">
                  Database
                </h1>
                <FormField
                  control={form.control}
                  name="databaseServer"
                  render={({ field }) => (
                    <FormItem className="mb-2">
                      <FormLabel>Database Server</FormLabel>
                      <FormControl>
                        <Input
                          disabled={props.isLoading}
                          readOnly
                          placeholder="Database Server"
                          {...field}
                        />
                      </FormControl>

                      <FormMessage />
                    </FormItem>
                  )}
                />
                <FormField
                  control={form.control}
                  name="databaseName"
                  render={({ field }) => (
                    <FormItem className="mb-2">
                      <FormLabel>Database Name</FormLabel>
                      <FormControl>
                        <Input
                          disabled={props.isLoading}
                          placeholder="Database Name"
                          {...field}
                        />
                      </FormControl>

                      <FormMessage />
                    </FormItem>
                  )}
                />
                <FormField
                  control={form.control}
                  name="databaseUser"
                  render={({ field }) => (
                    <FormItem className="mb-2">
                      <FormLabel>Database User</FormLabel>
                      <FormControl>
                        <Input
                          disabled={props.isLoading}
                          placeholder="Database User"
                          {...field}
                        />
                      </FormControl>

                      <FormMessage />
                    </FormItem>
                  )}
                />
                <FormField
                  control={form.control}
                  name="databasePassword"
                  render={({ field }) => (
                    <FormItem className="mb-2">
                      <FormLabel>Database Password</FormLabel>
                      <FormControl>
                        <Input
                          disabled={props.isLoading}
                          type="password"
                          placeholder="Database Password"
                          {...field}
                        />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
                <h1 className="text-xl font-bold text-black dark:text-white mb-2">
                  Package
                </h1>
                <FormItem className="mb-2">
                  <FormLabel>ClearBook Package</FormLabel>
                  <FormControl>
                    <Dropdown
                      id="clearbookPackage"
                      title="Clearbook Packages"
                      allCaps={true}
                      className={dropdownCss}
                    >
                      {packages}
                    </Dropdown>
                  </FormControl>
                  <FormMessage />
                </FormItem>
                <FormItem className="mb-2">
                  <FormLabel>Payment Package</FormLabel>
                  <FormControl>
                    <Dropdown
                      id="clearbookPaymentPackage"
                      title="Payment Packages"
                      className={dropdownCss}
                    >
                      {paymentPackages}
                    </Dropdown>
                  </FormControl>
                  <FormMessage />
                </FormItem>
                <h1 className="text-xl font-bold text-black dark:text-white mb-2">
                  FTP Connection
                </h1>
                <FormItem className="mb-2">
                  <FormLabel>FTP Connection</FormLabel>
                  <FormControl>
                    <Dropdown
                      id="clearbookFTPConnection"
                      title="FTP Connection"
                      className={dropdownCss}
                    >
                      {approvedConnections}
                    </Dropdown>
                  </FormControl>

                  <FormMessage />
                </FormItem>
              </div>
              <div className="col-start-1 col-end-7">
                {isDeploying == IsDeploying.authFailed && (
                  <h1 className="w-full text-xl font-bold text-black dark:text-white mb-2 py-1 px-2 rounded-md bg-red-500">
                    Authorization to deploy failed
                  </h1>
                )}
                <Button
                  disabled={props.isLoading}
                  className="w-full mt-4 cursor-pointer"
                  type="submit"
                >
                  Submit
                </Button>
              </div>
            </div>
          </form>
        </Form>
      )}

      {(isDeploying === IsDeploying.deploying ||
        isDeploying === IsDeploying.deploymentDone) &&
        currentStepIndex > -1 && (
          <div className="flex flex-col w-full max-w-2xl  gap-y-4">
            <Progress value={progress} />
          </div>
        )}
      {(isDeploying === IsDeploying.deploying ||
        isDeploying === IsDeploying.deploymentDone) &&
        currentStepIndex > -1 && (
          <div className="flex flex-col w-full max-w-2xl  gap-y-4">
            <ul>
              {isDeploying === IsDeploying.deploymentDone && (<span><p>View the deployed instance here: <a href={domainUrl} target="_blank" className="text-blue-500">View</a></p></span>)}
              {logs.map((log, index) => (
                <li key={index}>{log}</li>
              ))}
            </ul>
          </div>
        )}
    </div>
  );
}
